// FILE: src/components/PaymentRequired.jsx
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";

export default function PaymentRequired({ user }) {
  const navigate = useNavigate();
  const auth = getAuth();

  const isManager = !!(user && (user.role === "account_manager" || user.managerOnly === true));
  const businessTenantId =
    user?.businessAccount?.tenantId ||
    user?.primaryTenantId ||
    user?.tenantId ||
    "";

  const businessRole = String(
    user?.businessAccount?.role ||
    user?.tenantRole ||
    user?.role ||
    ""
  ).toLowerCase();

  const isBusinessOwner = !!(
    user &&
    ["owner", "hr_admin"].includes(businessRole)
  );

  const isBusinessMember = !!(
    user &&
    !!businessTenantId &&
    businessRole === "employee"
  );

  const isPersonalUser = !!(
    user &&
    !isManager &&
    !isBusinessOwner &&
    !isBusinessMember &&
    !businessTenantId
  );

  const businessBillingStatus = String(
    user?.businessAccount?.billingStatus ||
    user?.tenantBillingStatus ||
    ""
  ).toLowerCase();

  const hasPersonalAccess = !!(
    user &&
    (
      user?.isAdmin ||
      user?.subscriptionStatus === "active" ||
      user?.subscriptionStatus === "free" ||
      user?.personalAccount?.active === true
    )
  );

  const hasBusinessAccess = !!(
    user &&
    !!businessTenantId &&
    (
      ["owner", "hr_admin"].includes(businessRole) ||
      user?.businessAccount?.active === true ||
      ["active", "trial"].includes(businessBillingStatus)
    )
  );

  useEffect(() => {
    if (!user) return;

    if (hasBusinessAccess) {
      if (["owner", "hr_admin"].includes(businessRole) && businessTenantId) {
        navigate(`/tenant/${businessTenantId}`, { replace: true });
        return;
      }
      navigate("/", { replace: true });
      return;
    }

    if (hasPersonalAccess) {
      navigate("/", { replace: true });
    }
  }, [user, hasBusinessAccess, hasPersonalAccess, businessRole, businessTenantId, navigate]);

  async function handleSignOut() {
    try {
      await signOut(auth);
    } catch (e) {}
    navigate("/login", { replace: true });
  }

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={badgeWrap}>
          <span style={badge}>Access Restricted</span>
        </div>

        <h1 style={h1}>
          {isManager
            ? "Manager Access Pending"
            : isBusinessOwner
            ? "Complete Payment to Activate Your Tenant"
            : isBusinessMember
            ? "Business Access Pending"
            : isPersonalUser
            ? "Complete Payment to Activate Your Personal Account"
            : "Payment Required"}
        </h1>

        {isManager ? (
          <>
            <p style={p}>
              Your access is still awaiting verification. Once an administrator confirms the
              required conditions, your manager access will become available.
            </p>

            <div style={infoBox}>
              You can return to the manager area now, or sign out and come back later.
            </div>

            <div style={row}>
              <button onClick={() => navigate("/manager-select")} style={btnPrimary}>
                Go to Manager Access
              </button>
              <button onClick={handleSignOut} style={btnGhost} type="button">
                Sign out
              </button>
            </div>
          </>
        ) : isBusinessOwner ? (
          <>
            <p style={p}>
              Your owner account has been created, but your business workspace is still inactive
              because payment has not yet been completed.
            </p>

            <div style={infoBox}>
              Complete checkout in Stripe to activate your tenant and unlock the business dashboard.
            </div>

            <div style={row}>
              <Link to="/" style={btnPrimary}>
                View options
              </Link>
              <button onClick={handleSignOut} style={btnGhost} type="button">
                Sign out
              </button>
            </div>

            <p style={footNote}>
              If you already paid very recently, wait a few moments and sign in again once Stripe
              and the webhook update your tenant status.
            </p>
          </>
        ) : isBusinessMember ? (
          <>
            <p style={p}>
              Your business account membership has been recognised, but the tenant workspace is not active yet.
            </p>

            <div style={infoBox}>
              Please ask your tenant owner or administrator to complete billing activation before you try again.
            </div>

            <div style={row}>
              <button onClick={handleSignOut} style={btnGhost} type="button">
                Sign out
              </button>
            </div>

            <p style={footNote}>
              Once the tenant subscription becomes active, your business access should work automatically.
            </p>
          </>
        ) : isPersonalUser ? (
          <>
            <p style={p}>
              Your personal account has been created, but it is not active yet because payment has
              not been completed.
            </p>

            <div style={infoBox}>
              Choose a personal plan and complete checkout in Stripe to activate your account.
            </div>

            <div style={row}>
              <Link to="/" style={btnPrimary}>
                View options
              </Link>
              <button onClick={handleSignOut} style={btnGhost} type="button">
                Sign out
              </button>
            </div>

            <p style={footNote}>
              If you already paid very recently, wait a few moments and sign in again once Stripe
              and the webhook finish updating your account.
            </p>
          </>
        ) : (
          <>
            <p style={p}>
              We could not find an active subscription for this account. Please choose a plan to
              continue.
            </p>

            <div style={row}>
              <Link to="/plans" style={btnPrimary}>
                Choose a plan
              </Link>
              <button onClick={handleSignOut} style={btnGhost} type="button">
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* styles */
const wrap = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  background: "#f6f7fb",
};

const card = {
  width: "100%",
  maxWidth: 680,
  background: "#fff",
  border: "1px solid #e8ebf3",
  borderRadius: 18,
  boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
  padding: 32,
  textAlign: "center",
};

const badgeWrap = {
  marginBottom: 12,
};

const badge = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 12px",
  borderRadius: 999,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#b45309",
  fontWeight: 700,
  fontSize: 13,
};

const h1 = {
  margin: "0 0 14px 0",
  fontSize: 34,
  lineHeight: 1.15,
  color: "#2a0516",
  fontWeight: 800,
};

const p = {
  margin: "0 auto 18px auto",
  color: "#4b5563",
  fontSize: 17,
  lineHeight: 1.7,
  maxWidth: 560,
};

const infoBox = {
  margin: "0 auto 22px auto",
  maxWidth: 560,
  background: "#faf7fb",
  border: "1px solid #eadff0",
  borderRadius: 14,
  padding: "16px 18px",
  color: "#5b4b5f",
  fontSize: 15,
  lineHeight: 1.6,
};

const row = {
  display: "flex",
  gap: 12,
  justifyContent: "center",
  flexWrap: "wrap",
};

const btnPrimary = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#2a0516",
  color: "#fff",
  padding: "12px 18px",
  borderRadius: 10,
  border: "1px solid #2a0516",
  textDecoration: "none",
  cursor: "pointer",
  fontWeight: 700,
  minWidth: 160,
};

const btnGhost = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fff",
  color: "#111827",
  border: "1px solid #d7dce8",
  padding: "12px 18px",
  borderRadius: 10,
  textDecoration: "none",
  cursor: "pointer",
  fontWeight: 700,
  minWidth: 160,
};

const footNote = {
  margin: "18px auto 0 auto",
  maxWidth: 540,
  color: "#657899",
  fontSize: 14,
  lineHeight: 1.6,
};
